//
// Attributes QrCodeTemplate validation errors to the field/section of QrStyleForm
// that owns them. Both direct attributes (name, colors, logo, size) and style
// errors now arrive from Ruby already keyed by field name -- StyleConfig validates
// typed attributes, so there is no JSON pointer to parse.
//
// NOT the same module as terminus-hub's styleErrorTabs.ts, despite the name. That one
// maps camelCase qr-code-styling pointer roots (dotsOptions, cornersSquareOptions) by
// regex over the message string, against a different backend shape and a tab set that
// includes `frame`. This file's keys are StyleConfig's snake_case attributes and it
// parses nothing. They are not interchangeable and neither constrains the other.

export const GENERAL = 'general';

// fieldKey -> location. Real tabs: colors, shapes, logo. `general` is the always-
// visible section above the tabs (Name / Default download size).
export const LOCATION = {
  name: 'general',
  default_download_size: 'general',
  foreground_color: 'colors',
  background_color: 'colors',
  gradient: 'colors',
  eye_color: 'colors',
  transparency: 'colors',
  module_shape: 'shapes',
  eye_frame_shape: 'shapes',
  eye_ball_shape: 'shapes',
  logo: 'logo',
  logo_clip: 'logo',
  logo_size_ratio: 'logo',
  logo_clip_padding: 'logo',
  logo_remove_background: 'logo',
};

export function locationForKey(key) {
  return LOCATION[key] || GENERAL;
}

// Rails hands us display-ready messages (qr_code_template_helper.rb
// #qr_style_form_errors): bare, already carrying the "Color" noun for the two fields
// whose label does not name it. Sentence-casing is all that is left, and it lives here
// rather than in Ruby only because it is presentation with no knowledge of the model.
//
// This used to strip a per-key prefix back off a full_messages_for string. Ruby knows
// the attribute and the message source exactly; matching its human_attribute_name
// output with startsWith was guessing at something the generator already knew, and
// would have failed silently the first time a locale entry or column name changed.
export function normalizeMessage(message) {
  return message.charAt(0).toUpperCase() + message.slice(1);
}

// Flatten { fieldKey: [messages] } to { fieldKey: [firstMessage] }. Ruby already
// attributes every error to a field, so this only collapses each field to its
// single most relevant message -- the form shows one message per field.
export function normalizeErrors(initialErrors) {
  const out = {};
  Object.entries(initialErrors || {}).forEach(([key, messages]) => {
    if (messages && messages.length > 0) {
      out[key] = [normalizeMessage(messages[0])];
    }
  });
  return out;
}

// Keys with no entry in LOCATION — `_general` (StyleConfig's bucket for schema
// failures it cannot attribute to a field) plus anything the frontend does not
// know about. They own no field and no tab, so QrGeneralErrors renders them.
// Note this tests LOCATION membership, NOT locationForKey(): `name` and
// `default_download_size` legitimately resolve to 'general' and are excluded.
export function unmappedErrorKeys(errors) {
  return Object.keys(errors || {}).filter((key) => LOCATION[key] === undefined);
}
